import React, { useState, useEffect } from 'react'
import { Calendar, Clock, ChevronLeft, ChevronRight, X, Check } from 'lucide-react'

export interface DateRange {
  startDate: string
  endDate: string
  /**
   * Rolling windows ("last 24 hours") carry the exact instants. The date-only
   * fields stay authoritative for the calendar grid and for every preset that
   * really means whole days.
   */
  startAt?: string
  endAt?: string
  label?: string
}

/** A window ending now, used as the default analysis range. */
export const rollingHourRange = (hours = 24): DateRange => {
  const end = new Date()
  const start = new Date(end.getTime() - hours * 60 * 60 * 1000)
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
    startAt: start.toISOString(),
    endAt: end.toISOString(),
    label: hours === 24 ? 'Last 24 hours' : `Last ${hours} hours`
  }
}

/**
 * The calendar day, midnight to now — the default window since 12 Oct. A
 * rolling 24 h mixed yesterday afternoon into this morning's picture, which
 * the operators read as "what happened today" and found wrong.
 */
export const todayRange = (): DateRange => {
  const now = new Date()
  const start = new Date(now)
  start.setHours(0, 0, 0, 0)
  const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  return {
    startDate: day,
    endDate: day,
    startAt: start.toISOString(),
    endAt: now.toISOString(),
    label: 'Today'
  }
}

interface CalendarModalProps {
  isOpen: boolean
  onClose: () => void
  value: DateRange
  onChange: (range: DateRange) => void
  title?: string
}

interface PresetPeriod {
  label: string
  days: number
  key: string
  /** Set for rolling windows, which are not a whole number of calendar days. */
  hours?: number
}

const CalendarModal: React.FC<CalendarModalProps> = ({
  isOpen,
  onClose,
  value,
  onChange,
  title = "Select Date Range"
}) => {
  const [selectedRange, setSelectedRange] = useState<DateRange>(value)
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectingStart, setSelectingStart] = useState(true)

  // Preset periods for quick selection
  const presetPeriods: PresetPeriod[] = [
    { label: 'Last 24 hours', days: 1, key: '24h', hours: 24 },
    { label: 'Today', days: 0, key: 'today' },
    { label: 'Yesterday', days: 1, key: 'yesterday' },
    { label: 'Last 7 days', days: 7, key: '7days' },
    { label: 'Last 15 days', days: 15, key: '15days' },
    { label: 'Last 30 days', days: 30, key: '30days' },
    { label: 'Last 3 months', days: 90, key: '3months' },
    { label: 'Last 6 months', days: 180, key: '6months' },
    { label: 'This year', days: 365, key: 'year' }
  ]

  useEffect(() => {
    if (isOpen) {
      setSelectedRange(value)
      setCurrentMonth(new Date(value.startDate || new Date()))
    }
  }, [isOpen, value])

  // Apply preset period
  const applyPreset = (preset: PresetPeriod) => {
    if (preset.hours) {
      setSelectedRange(rollingHourRange(preset.hours))
      return
    }

    const endDate = new Date()
    const startDate = new Date()

    if (preset.key === 'today') {
      startDate.setHours(0, 0, 0, 0)
      endDate.setHours(23, 59, 59, 999)
    } else if (preset.key === 'yesterday') {
      startDate.setDate(startDate.getDate() - 1)
      startDate.setHours(0, 0, 0, 0)
      endDate.setDate(endDate.getDate() - 1)
      endDate.setHours(23, 59, 59, 999)
    } else {
      startDate.setDate(startDate.getDate() - preset.days)
      startDate.setHours(0, 0, 0, 0)
      endDate.setHours(23, 59, 59, 999)
    }

    const range: DateRange = {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      label: preset.label
    }

    setSelectedRange(range)
  }

  // Navigate months
  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth)
    if (direction === 'next') {
      newMonth.setMonth(newMonth.getMonth() + 1)
    } else {
      newMonth.setMonth(newMonth.getMonth() - 1)
    }
    setCurrentMonth(newMonth)
  }

  // Get days in month
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days = []

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day))
    }

    return days
  }

  // Handle day click
  const handleDayClick = (date: Date) => {
    const dateString = date.toISOString().split('T')[0]
    
    // Picking a day means whole days, so any rolling instants are dropped.
    if (selectingStart) {
      setSelectedRange(prev => ({
        ...prev,
        startDate: dateString,
        startAt: undefined,
        endAt: undefined,
        label: undefined
      }))
      setSelectingStart(false)
    } else {
      const startDate = new Date(selectedRange.startDate)
      if (date >= startDate) {
        setSelectedRange(prev => ({
          ...prev,
          endDate: dateString,
          startAt: undefined,
          endAt: undefined,
          label: undefined
        }))
      } else {
        // If end date is before start date, swap them
        setSelectedRange(prev => ({
          ...prev,
          startDate: dateString,
          endDate: prev.startDate,
          startAt: undefined,
          endAt: undefined,
          label: undefined
        }))
      }
      setSelectingStart(true)
    }
  }

  // Check if date is in selected range
  const isDateInRange = (date: Date) => {
    if (!selectedRange.startDate || !selectedRange.endDate) return false
    const dateString = date.toISOString().split('T')[0]
    return dateString >= selectedRange.startDate && dateString <= selectedRange.endDate
  }

  // Check if date is start or end of range
  const isDateRangeEnd = (date: Date) => {
    const dateString = date.toISOString().split('T')[0]
    return dateString === selectedRange.startDate || dateString === selectedRange.endDate
  }

  // Apply selection and close
  const handleApply = () => {
    if (selectedRange.startDate && selectedRange.endDate) {
      // Check if date range is too large (warn if > 7 days)
      const startTime = new Date(selectedRange.startDate).getTime()
      const endTime = new Date(selectedRange.endDate).getTime()
      const daysDiff = Math.ceil((endTime - startTime) / (1000 * 60 * 60 * 24))
      
      if (daysDiff > 7) {
        console.warn(`⚠️ Large date range selected: ${daysDiff} days. This may cause slow loading.`)
      }
      
      onChange(selectedRange)
      onClose()
    }
  }

  // Format month/year display
  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
  }

  if (!isOpen) return null

  const days = getDaysInMonth(currentMonth)
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[300] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full min-w-[320px] max-w-[600px] max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4">
          {/* Quick Periods Dropdown */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Quick Periods</label>
            <select
              onChange={(e) => {
                const preset = presetPeriods.find(p => p.key === e.target.value)
                if (preset) {
                  applyPreset(preset)
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              defaultValue=""
            >
              <option value="">Select a period...</option>
              {presetPeriods.map((preset) => (
                <option key={preset.key} value={preset.key}>
                  {preset.label}
                </option>
              ))}
            </select>
          </div>

          {/* Current Selection Display */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-sm">
              <span className="text-gray-600">Selected: </span>
              <span className="font-medium">
                {selectedRange.label || (
                  selectedRange.startDate && selectedRange.endDate
                    ? `${new Date(selectedRange.startDate).toLocaleDateString('en-GB')} - ${new Date(selectedRange.endDate).toLocaleDateString('en-GB')}`
                    : 'No period selected'
                )}
              </span>
            </div>
          </div>

          {/* Calendar */}
          <div>
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigateMonth('prev')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              
              <h3 className="text-base font-semibold text-gray-900 capitalize">
                {formatMonthYear(currentMonth)}
              </h3>
              
              <button
                onClick={() => navigateMonth('next')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* Week Days Header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map(day => (
                <div key={day} className="p-2 text-center text-xs font-medium text-gray-500">
                  {day.slice(0, 3)}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((date, index) => {
                if (!date) {
                  return <div key={index} className="p-2"></div>
                }

                const isToday = date.toDateString() === new Date().toDateString()
                const isInRange = isDateInRange(date)
                const isRangeEnd = isDateRangeEnd(date)

                return (
                  <button
                    key={index}
                    onClick={() => handleDayClick(date)}
                    className={`
                      p-2 text-sm rounded-lg transition-colors relative min-h-[36px]
                      ${isToday ? 'ring-2 ring-blue-500' : ''}
                      ${isInRange && !isRangeEnd ? 'bg-blue-100 text-blue-700' : ''}
                      ${isRangeEnd ? 'bg-blue-600 text-white' : ''}
                      ${!isInRange && !isRangeEnd ? 'hover:bg-gray-100' : ''}
                    `}
                  >
                    {date.getDate()}
                    {isToday && (
                      <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-blue-500 rounded-full"></div>
                    )}
                  </button>
                )
              })}
            </div>

            {/* Selection Status */}
            <div className="mt-3 p-2 bg-blue-50 rounded-lg">
              <div className="text-xs text-blue-700 text-center">
                {selectingStart ? (
                  <span>📅 Select the <strong>start date</strong></span>
                ) : (
                  <span>📅 Select the <strong>end date</strong></span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-center justify-between p-3 border-t border-gray-200 bg-gray-50 space-y-2 sm:space-y-0">
          <button
            onClick={() => {
              setSelectedRange({ startDate: '', endDate: '', label: undefined })
              setSelectingStart(true)
            }}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Clear
          </button>
          
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors order-2 sm:order-1"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={!selectedRange.startDate || !selectedRange.endDate}
              className="px-6 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 order-1 sm:order-2"
            >
              <Check className="w-4 h-4" />
              <span>Apply</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CalendarModal
